import React, { useMemo, useState } from "react"
import { Search, Filter, ChevronDown, FileDown, X } from "lucide-react"
import { CSVLink } from "react-csv"
import type { CrudFilterState, FilterValue, QuickFilterDef } from "../../types/crudFilters.ts"
import { getActiveQuickFilterChips, type ActiveFilterChip } from "../../utils/crudFilterParams.ts"
import { CrudQuickFilter } from "./CrudQuickFilter.tsx"
import { CrudFilterSheet } from "./CrudFilterSheet.tsx"

type CrudTableToolbarProps = {
  globalSearch: string
  onGlobalSearchChange: (value: string) => void
  quickFilters?: QuickFilterDef[]
  filterState?: CrudFilterState
  onQuickFilterChange?: (field: string, value: FilterValue) => void
  advancedFilterChips?: ActiveFilterChip[]
  onRemoveAdvancedFilter?: (field: string) => void
  hasActiveFilters?: boolean
  onClearFilters?: () => void
  showAdvanced: boolean
  onToggleAdvanced: () => void
  exportData?: any[]
  exportFilename?: string
  hideExport?: boolean
  advancedFiltersPanel?: React.ReactNode
}

function FilterChip({
  label,
  value,
  onRemove,
}: {
  label: string
  value: string
  onRemove: () => void
}) {
  return (
    <span className="inline-flex h-7 max-w-[14rem] items-center gap-1 rounded-md border border-electric-blue/20 bg-electric-blue/10 pl-2.5 pr-1 text-xs text-deep-ocean">
      <span className="truncate">
        <span className="text-slate-gray">{label}:</span> {value}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="shrink-0 rounded p-0.5 text-slate-gray hover:bg-electric-blue/20 hover:text-deep-ocean"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

export function CrudTableToolbar({
  globalSearch,
  onGlobalSearchChange,
  quickFilters = [],
  filterState = {},
  onQuickFilterChange,
  advancedFilterChips = [],
  onRemoveAdvancedFilter,
  hasActiveFilters = false,
  onClearFilters,
  showAdvanced,
  onToggleAdvanced,
  exportData = [],
  exportFilename = "export.csv",
  hideExport = false,
  advancedFiltersPanel,
}: CrudTableToolbarProps) {
  const [sheetOpen, setSheetOpen] = useState(false)

  const quickFilterChips = useMemo(
    () => getActiveQuickFilterChips(quickFilters, filterState),
    [quickFilters, filterState],
  )

  const activeCount =
    quickFilterChips.length + advancedFilterChips.length + (globalSearch.trim() ? 1 : 0)

  const showActiveBar = activeCount > 0

  const handleClearAll = () => {
    onClearFilters?.()
    onGlobalSearchChange("")
  }

  const exportLink = !hideExport ? (
    <CSVLink
      data={exportData}
      filename={exportFilename}
      className={`inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-colors sm:h-9 sm:flex-none sm:px-2.5 ${
        exportData.length === 0
          ? "cursor-not-allowed bg-slate-gray/10 text-slate-gray/50 pointer-events-none"
          : "bg-electric-blue text-white hover:bg-btn-hover shadow-sm"
      }`}
    >
      <FileDown className="h-4 w-4 shrink-0" />
      <span className="md:hidden">Export</span>
      <span className="hidden md:inline">
        Export{exportData.length > 0 ? ` (${exportData.length})` : ""}
      </span>
    </CSVLink>
  ) : null

  return (
    <div className="space-y-2">
      {/* Mobile toolbar */}
      <div className="space-y-2 md:hidden">
        <div className="relative">
          <input
            type="text"
            value={globalSearch}
            onChange={(e) => onGlobalSearchChange(e.target.value)}
            className={`h-10 w-full rounded-lg border bg-white pl-9 pr-8 text-base text-deep-ocean placeholder-slate-gray/50 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-electric-blue/30 ${
              globalSearch.trim()
                ? "border-electric-blue/40 bg-electric-blue/[0.03]"
                : "border-slate-gray/30"
            }`}
            placeholder="Search..."
          />
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-gray/60" />
          {globalSearch.trim() ? (
            <button
              type="button"
              onClick={() => onGlobalSearchChange("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-gray hover:bg-slate-gray/10 hover:text-deep-ocean"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className={`inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-lg border text-sm font-medium ${
              activeCount > 0 || showAdvanced
                ? "border-electric-blue/30 bg-electric-blue/10 text-electric-blue"
                : "border-slate-gray/20 bg-white text-slate-gray"
            }`}
          >
            <Filter className="h-4 w-4 shrink-0" />
            Filters{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
          {exportLink}
        </div>
      </div>

      {/* Desktop toolbar */}
      <div className="hidden items-center gap-2 rounded-lg border border-slate-gray/10 bg-white px-3 py-2.5 shadow-sm md:flex">
        <div className="relative w-48 shrink-0">
          <input
            type="text"
            value={globalSearch}
            onChange={(e) => onGlobalSearchChange(e.target.value)}
            className={`h-9 w-full rounded-lg border bg-white pl-9 pr-8 text-sm text-deep-ocean placeholder-slate-gray/50 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-electric-blue/30 ${
              globalSearch.trim()
                ? "border-electric-blue/40 bg-electric-blue/[0.03]"
                : "border-slate-gray/30"
            }`}
            placeholder="Search..."
          />
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-gray/60" />
          {globalSearch.trim() ? (
            <button
              type="button"
              onClick={() => onGlobalSearchChange("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-gray hover:bg-slate-gray/10 hover:text-deep-ocean"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>

        <div className="hidden w-px shrink-0 bg-slate-gray/15 sm:block md:h-6" aria-hidden />

        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {quickFilters.map((qf) => (
            <CrudQuickFilter
              key={qf.id}
              def={qf}
              filterState={filterState}
              onChange={onQuickFilterChange ?? (() => {})}
            />
          ))}
        </div>

        <div className="ml-1 flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onToggleAdvanced}
            className={`relative flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-sm transition-colors ${
              showAdvanced || advancedFilterChips.length > 0
                ? "border-electric-blue/30 bg-electric-blue/10 text-electric-blue"
                : "border-slate-gray/20 bg-white text-slate-gray hover:bg-light-sky/50"
            }`}
          >
            <Filter className="h-4 w-4 shrink-0" />
            <span>More filters</span>
            {advancedFilterChips.length > 0 ? (
              <span className="inline-flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-electric-blue px-1 text-[10px] font-medium leading-none text-white">
                {advancedFilterChips.length}
              </span>
            ) : null}
            <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          </button>
          {exportLink}
        </div>
      </div>

      <CrudFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        activeCount={activeCount}
        onClearAll={onClearFilters ? handleClearAll : undefined}
      >
        <div className="space-y-3">
          {quickFilters.map((qf) => (
            <CrudQuickFilter
              key={qf.id}
              def={qf}
              filterState={filterState}
              onChange={onQuickFilterChange ?? (() => {})}
              fullWidth
            />
          ))}
          {advancedFiltersPanel ? (
            <div className="border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={onToggleAdvanced}
                className="mb-3 flex w-full items-center justify-between rounded-lg border border-slate-gray/20 px-3 py-2 text-sm font-medium text-deep-ocean"
              >
                Column filters
                <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
              </button>
              {showAdvanced ? advancedFiltersPanel : null}
            </div>
          ) : null}
        </div>
      </CrudFilterSheet>

      {showActiveBar ? (
        <div className="flex flex-wrap items-center gap-2 px-1">
          <span className="shrink-0 text-xs text-slate-gray">
            {activeCount} filter{activeCount === 1 ? "" : "s"} applied
          </span>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {globalSearch.trim() ? (
              <FilterChip
                label="Search"
                value={globalSearch.trim()}
                onRemove={() => onGlobalSearchChange("")}
              />
            ) : null}
            {quickFilterChips.map((chip) => (
              <FilterChip
                key={chip.field}
                label={chip.label}
                value={chip.displayValue}
                onRemove={() => onQuickFilterChange?.(chip.field, null)}
              />
            ))}
            {advancedFilterChips.map((chip) => (
              <FilterChip
                key={chip.field}
                label={chip.label}
                value={chip.displayValue}
                onRemove={() => onRemoveAdvancedFilter?.(chip.field)}
              />
            ))}
          </div>
          {onClearFilters ? (
            <button
              type="button"
              onClick={handleClearAll}
              className="ml-auto inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium text-coral-red hover:bg-coral-red/5 hover:text-coral-red/80"
            >
              <X className="h-3 w-3" />
              Clear all
            </button>
          ) : null}
        </div>
      ) : hasActiveFilters && onClearFilters ? (
        <div className="flex items-center px-1">
          <button
            type="button"
            onClick={handleClearAll}
            className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-coral-red hover:bg-coral-red/5 hover:text-coral-red/80"
          >
            <X className="h-3 w-3" />
            Clear all filters
          </button>
        </div>
      ) : null}
    </div>
  )
}
